import { Button } from "@/components/ui/button";
import { defaultButtonClasses } from "@/constants/ui";
import { CalendarDays, Check, ClockFading, DoorOpen, Lock, RectangleEllipsis } from "lucide-react";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import { DisabledFeatureTooltip } from "@/components/tpr/DisabledFeatureTooltip";
import { useBookingActionFlags } from "@/hooks/useBookingActionFlags";

type ChangeWhatProps = {
  onClose?: () => void;
};

const ChangeWhat = ({ onClose }: ChangeWhatProps) => {
  const { resetFlow, setChangingWhat, changingWhat, navigateTo, booking } = useChangeBookingFlow();
  const actionFlags = useBookingActionFlags();

  // Map of icon names to Lucide components
  const iconMap = {
    Lock: Lock,
    RectangleEllipsis: RectangleEllipsis,
    CalendarDays: CalendarDays,
    ClockFading: ClockFading,
    DoorOpen: DoorOpen,
  };

  const actionTypes: Array<{
    value: "datetime" | "duration" | "room" | null;
    label: string;
    description: string;
    icon?: string;
  }> = [
    {
      value: "datetime",
      label: "Change Date and/or Time",
      description: "Move your booking to another day or start time",
      icon: "CalendarDays",
    },
    // Slots are a fixed 3-hour block (R13), so their duration can't be amended — omit the option.
    ...(booking?.type === "slot"
      ? []
      : [
          {
            value: "duration" as const,
            label: "Change Duration",
            description: "Adjust how long your booking lasts",
            icon: "ClockFading",
          },
        ]),
    {
      value: "room",
      label: "Change the Room",
      description: "Change the room of your booking",
      icon: "DoorOpen",
    },
  ];

  const renderContent = () => {
    return (
      <div className="flex flex-col gap-4">
        {/* Consecutive Chain Warning */}
        {booking?.consecutiveChainMetadata && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="font-medium text-blue-800 text-sm">Consecutive Booking Notice:</p>
            <p className="text-sm text-blue-700 mt-1">
              This booking is part of a continuous block. Changing or cancelling it will affect your total charged time for the day.
            </p>
          </div>
        )}
        <div>
          {/* Booking Type Selection */}
          <div>
            {/* <label className="block text-sm font-medium text-gray-700 mb-2">Booking Type</label> */}
            <div className="space-y-2">
              {actionTypes.map((type) => {
                const gated = type.value ? !actionFlags[type.value] : false;
                return (
                  <DisabledFeatureTooltip key={type.value} disabled={gated}>
                    <button
                      onClick={() => !gated && setChangingWhat?.(type.value)}
                      disabled={gated}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        changingWhat === type.value
                          ? "border-indigo-600 bg-indigo-50"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:cursor-pointer"
                      } ${gated ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          {type.icon && iconMap[type.icon as keyof typeof iconMap] && (
                            <div
                              className={`flex-shrink-0 ${changingWhat === type.value ? "text-indigo-600" : "text-gray-400"}`}
                            >
                              {(() => {
                                const IconComponent = iconMap[type.icon as keyof typeof iconMap];
                                return <IconComponent className="w-5 h-5" />;
                              })()}
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-semibold ${changingWhat === type.value ? "text-indigo-900" : "text-gray-900"}`}
                              >
                                {type.label}
                              </span>
                              {changingWhat === type.value && <Check className="w-5 h-5 text-indigo-600" />}
                            </div>
                            <p
                              className={`text-sm mt-1 ${changingWhat === type.value ? "text-indigo-700" : "text-gray-600"}`}
                            >
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  </DisabledFeatureTooltip>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-6">
          <Button
            className={`w-full ` + defaultButtonClasses}
            onClick={() => changingWhat && navigateTo(changingWhat)}
            disabled={!changingWhat}
          >
            Next
          </Button>
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">What would you like to change?</h2>
        <p className="text-gray-600 text-xs">
          You can change a booking date, time, duration or room where there is availablility and you are not within 24
          hours of the booking time.
        </p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">What would you like to change?</h2>
          <p className="mb-2 text-gray-600 text-sm">
            You can change a booking date, time, duration or room where there is availablility and you are not within 24
            hours of the booking time.
          </p>
        </div>

        {/* Right column - Selection */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          {renderContent()}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">{renderContent()}</div>
    </div>
  );
};

export default ChangeWhat;
